import MeterBar from '@/components/console/meter-bar';
import ModeBadge from '@/components/console/mode-badge';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import MessageTable from '@/pages/apps/components/message-table';
import type { AppShell, Message, ProviderMode } from '@/types/platform';

interface MessagesIndexProps {
  app: AppShell;
  messages: Message[];
  mailMode: ProviderMode;
  sender: { name: string; from: string; kind: 'shared' | 'from_name' | 'domain' };
  emails: { used: number; limit: number; hardCap: boolean };
}

const MessagesIndex = ({ app, messages, mailMode, sender, emails }: MessagesIndexProps) => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Messages"
        description="Every email this app sent, or tried to send, newest first."
        badges={<ModeBadge mode={mailMode} />}
      />

      {mailMode === 'demo' ? (
        <p className="text-muted-foreground text-sm">
          Demo mode: email is recorded here but not sent.
        </p>
      ) : null}

      <div className="grid gap-6 sm:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>
              <h2>Sent as</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium break-words">{sender.from}</p>
            <p className="text-muted-foreground mt-1 text-xs">
              This is who your customers see the email is from.
            </p>
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>
              <h2>This month</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <MeterBar
              row={{
                key: 'emails',
                label: 'Emails sent',
                used: emails.used,
                limit: emails.limit,
                unit: 'count',
              }}
            />
            {emails.hardCap ? (
              <p className="text-muted-foreground text-xs">
                At the limit, sending stops until next month.
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card className="min-w-0">
        <CardContent>
          {messages.length === 0 ? (
            <EmptyState
              title="No messages yet"
              description="When you send an invoice or a receipt goes out, it is listed here."
            />
          ) : (
            <MessageTable appId={app.id} messages={messages} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MessagesIndex;
